/**
 * Whether the caller's firm has a workspace (Microsoft 365 / Google Workspace)
 * connected at all (2026-10-12).
 *
 * `useMailBrand()` deliberately cannot answer this: it maps "not connected"
 * and "not known yet" to the same Microsoft wording, so a label never
 * flickers. A button that can only work against a connected workspace —
 * "Open in Word / Google Docs" — needs the distinction: with no connection
 * there is nowhere to create the editable copy, so the click can only fail.
 *
 * Answers `false` ONLY when `/api/auth/me` has positively said the firm has
 * no connection (`workspaceProvider: null`). Loading, an older API that omits
 * the field, or no QueryClient above the component all answer `true`, so a
 * connected firm never loses the button while the profile loads.
 *
 * Kept out of `use-mail-brand.ts` on purpose: many tests mock that module
 * with only `useMailBrand`, and a second export there would be `undefined`
 * under those mocks.
 */
import { useContext } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { useMe } from './use-users-me';

export function useWorkspaceConnected(): boolean {
  // Same guard as useMailBrand: `useQuery` throws without a provider, and
  // whether one is present is fixed by the tree above, so hook order holds.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  if (!hasQueryClient) return true;

  const { data } = useMe();
  return data?.workspaceProvider !== null;
}
